/**
 * 租戶網路硬碟（#23 增量 2）· fork MyDrivePage 模式的 org-owned 視圖。
 * baseline：list / upload / 建資料夾 / 下載 / 刪除 + 雙配額 QuotaBar + SYSTEM_ADMIN admin panel。
 * 不含（增量 3+）：webdav / batch / thumbnail / 分享（Phase 2 = #24）。
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import { useAuth } from '../../../contexts/AuthContext';
import FunctionPageHeader from '../../../components/FunctionPageHeader';
import { getOrganizations } from '../../../services/organizationService';
import {
  listFiles, uploadFile, createFolder, deleteFile, downloadFile,
  getQuota, getOrgQuota, adjustQuota,
  TenantDriveFile, TenantDriveQuota,
} from './services/tenantDriveService';

const FUNC = 'ee_tenant_drive';
const SYSTEM_ADMIN_ORG_ID = 1;
const GiB = 1024 ** 3;

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < GiB) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / GiB).toFixed(2)} GB`;
}

const TenantDrivePage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();
  const { user } = useAuth();

  const canRead = hasPermission(FUNC, 'read');
  const canCreate = hasPermission(FUNC, 'create');
  const canUpdate = hasPermission(FUNC, 'update');   // 配額管理 gate（admin panel 需 update）
  const canDelete = hasPermission(FUNC, 'delete');
  const isSystemAdmin = user?.organization_id === SYSTEM_ADMIN_ORG_ID;

  const [files, setFiles] = useState<TenantDriveFile[]>([]);
  const [quota, setQuota] = useState<TenantDriveQuota | null>(null);
  const [loading, setLoading] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);
  const hasInit = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, q] = await Promise.all([listFiles(), getQuota()]);
      setFiles(f.items);
      setQuota(q);
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [showApiError]);

  useEffect(() => {
    if (!permLoading && canRead && !hasInit.current) {
      hasInit.current = true;
      load();
    }
  }, [permLoading, canRead, load]);

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;
    try {
      for (const file of Array.from(fileList)) {
        await uploadFile(file, file.name);
      }
      showSuccess('SYS020001', { name: t('tenantDrive.upload', '上傳') });
      await load();
    } catch (err) { showApiError(err); }
  };

  const handleNewFolder = async () => {
    const name = window.prompt(t('tenantDrive.folderName', '資料夾名稱'));
    if (!name || !name.trim()) return;
    try {
      await createFolder(name.trim());
      showSuccess('SYS020001', { name: name.trim() });
      await load();
    } catch (err) { showApiError(err); }
  };

  const handleDelete = async (f: TenantDriveFile) => {
    if (!window.confirm(t('common.confirmDelete', { name: f.filename }))) return;
    try {
      await deleteFile(f.id);
      showSuccess('SYS020003', { name: f.filename });   // 通用「{name}刪除成功」（SYS310033 是「聯絡人刪除成功」，語意不符）
      await load();
    } catch (err) { showApiError(err); }
  };

  const handleDownload = async (f: TenantDriveFile) => {
    try { await downloadFile(f.id, f.filename); }  // silent ok: 瀏覽器下載對話框即回饋
    catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!canRead) {
    return (
      <div>
        <FunctionPageHeader funcCode={FUNC} />
        <div style={{ padding: 24, color: '#d32f2f' }}>{t('common.noPermission', '您沒有權限存取此功能')}</div>
      </div>
    );
  }

  const usedPct = quota && quota.quota_bytes ? Math.min(100, (quota.total_used / quota.quota_bytes) * 100) : 0;
  const btn: React.CSSProperties = { padding: '6px 14px', border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff', background: '#1976d2' };
  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: '#f5f5f5', borderBottom: '1px solid #ddd' };
  const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #eee' };

  return (
    <div>
      <FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: '12px 24px' }}>

        {/* ── 雙配額 QuotaBar：租戶總量 pool + 成員明細 ── */}
        {quota && (
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16, background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong>{t('tenantDrive.tenantQuota', '租戶總量')}</strong>
              <span style={{ color: '#555' }}>
                {fmtSize(quota.total_used)} / {fmtSize(quota.quota_bytes)}（{usedPct.toFixed(1)}%）
              </span>
            </div>
            <div style={{ height: 10, background: '#e0e0e0', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{ width: `${usedPct}%`, height: '100%', background: usedPct >= 90 ? '#d32f2f' : '#1976d2' }} />
            </div>
            <div style={{ fontSize: 13, color: '#777', marginTop: 6 }}>
              {t('tenantDrive.orgDrive', '租戶硬碟')} {fmtSize(quota.org_drive_used)} ·
              {' '}{t('tenantDrive.membersUsed', '成員個人硬碟合計')} {fmtSize(quota.members_used)} ·
              {' '}{t('tenantDrive.userDefault', '每人基本')} {fmtSize(quota.user_default_bytes)}
            </div>
            <button onClick={() => setShowMembers(s => !s)}
              style={{ marginTop: 8, padding: '2px 8px', border: '1px solid #ccc', borderRadius: 4, background: 'none', cursor: 'pointer', fontSize: 13 }}>
              {showMembers ? t('common.close', '收合') : t('tenantDrive.memberDetail', '成員明細')}（{quota.members.length}）
            </button>
            {showMembers && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                <thead><tr>
                  <th style={th}>{t('user.username', '使用者')}</th>
                  <th style={th}>{t('tenantDrive.used', '已用')}</th>
                </tr></thead>
                <tbody>
                  {quota.members.map(m => (
                    <tr key={m.user_id}><td style={td}>{m.username}</td><td style={td}>{fmtSize(m.used_bytes)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Toolbar ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button style={{ ...btn, opacity: canCreate ? 1 : 0.5 }} disabled={!canCreate}
            onClick={() => uploadRef.current?.click()}>+ {t('tenantDrive.upload', '上傳')}</button>
          <button style={{ ...btn, background: '#455a64', opacity: canCreate ? 1 : 0.5 }} disabled={!canCreate}
            onClick={handleNewFolder}>+ {t('tenantDrive.newFolder', '新增資料夾')}</button>
          <button style={{ ...btn, background: 'none', color: '#1976d2', border: '1px solid #1976d2' }}
            onClick={load}>{t('common.refresh', '重新整理')}</button>
          {isSystemAdmin && canUpdate && (
            <button style={{ ...btn, background: '#6a1b9a', marginLeft: 'auto' }}
              onClick={() => setShowAdmin(s => !s)}>{t('tenantDrive.adminPanel', '配額管理')}</button>
          )}
          <input ref={uploadRef} type="file" multiple hidden
            onChange={e => { handleUpload(e.target.files); e.target.value = ''; }} />
        </div>

        {/* ── SYSTEM_ADMIN 配額管理 panel（需 update 權）── */}
        {isSystemAdmin && canUpdate && showAdmin && <AdminQuotaPanel canUpdate={canUpdate} />}

        {/* ── 檔案表 ── */}
        {loading ? <div>{t('common.loading', '載入中...')}</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr>
              <th style={th}>{t('tenantDrive.filename', '名稱')}</th>
              <th style={th}>{t('tenantDrive.size', '大小')}</th>
              <th style={th}>{t('tenantDrive.type', '類型')}</th>
              <th style={th}>{t('common.actions', '操作')}</th>
            </tr></thead>
            <tbody>
              {files.map(f => (
                <tr key={f.id}>
                  <td style={td}>{f.is_directory ? '📁 ' : '📄 '}{f.filename}</td>
                  <td style={td}>{f.is_directory ? '—' : fmtSize(f.size_bytes)}</td>
                  <td style={td}>{f.is_directory ? t('tenantDrive.folder', '資料夾') : f.mime_type}</td>
                  <td style={td}>
                    {!f.is_directory && (
                      <button onClick={() => handleDownload(f)}
                        style={{ marginRight: 6, padding: '2px 10px', border: '1px solid #1976d2', color: '#1976d2', background: 'none', borderRadius: 4, cursor: 'pointer' }}>
                        {t('tenantDrive.download', '下載')}
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete(f)}
                        style={{ padding: '2px 10px', border: '1px solid #d32f2f', color: '#d32f2f', background: 'none', borderRadius: 4, cursor: 'pointer' }}>
                        {t('common.delete', '刪除')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {files.length === 0 && (
                <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#999', padding: 32 }}>
                  {t('tenantDrive.empty', '（無檔案）')}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ── SYSTEM_ADMIN 配額管理：列全租戶，inline 調 quota_gb / user_default_gb ──
const AdminQuotaPanel: React.FC<{ canUpdate: boolean }> = ({ canUpdate }) => {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const [rows, setRows] = useState<{ id: number; name: string; quota_gb: string; user_default_gb: string }[]>([]);
  const [saving, setSaving] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await getOrganizations();
        const withQuota = await Promise.all(list.map(async (o) => {
          let q: TenantDriveQuota | null = null;
          try { q = await getOrgQuota(o.id); } catch { /* silent ok: 個別租戶配額讀取失敗不阻斷清單 */ }
          return {
            id: o.id, name: o.org_name || `org#${o.id}`,
            quota_gb: q ? (q.quota_bytes / GiB).toFixed(0) : '',
            user_default_gb: q ? (q.user_default_bytes / GiB).toFixed(0) : '',
          };
        }));
        setRows(withQuota);
      } catch (err) { showApiError(err); }
    })();
  }, [showApiError]);

  const save = async (r: { id: number; quota_gb: string; user_default_gb: string }) => {
    setSaving(r.id);
    try {
      await adjustQuota(r.id, {
        quota_gb: r.quota_gb ? parseFloat(r.quota_gb) : undefined,
        user_default_gb: r.user_default_gb ? parseFloat(r.user_default_gb) : undefined,
      });
      showSuccess('SYS020001', { name: t('tenantDrive.adminPanel', '配額') });
    } catch (err) { showApiError(err); }
    finally { setSaving(null); }
  };

  const cell: React.CSSProperties = { padding: '6px 10px', borderBottom: '1px solid #eee' };
  const inp: React.CSSProperties = { width: 70, padding: '3px 6px', border: '1px solid #ccc', borderRadius: 4 };

  return (
    <div style={{ border: '1px solid #ce93d8', borderRadius: 8, padding: 12, marginBottom: 16, background: '#f8f0fc' }}>
      <strong>{t('tenantDrive.adminPanel', '租戶配額管理')}（SYSTEM_ADMIN）</strong>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
        <thead><tr>
          <th style={cell}>{t('users.organization', '組織')}</th>
          <th style={cell}>{t('tenantDrive.tenantQuota', '租戶總量')} (GB)</th>
          <th style={cell}>{t('tenantDrive.userDefault', '每人基本')} (GB)</th>
          <th style={cell}>{t('common.actions', '操作')}</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td style={cell}>{r.name}</td>
              <td style={cell}>
                <input style={inp} value={r.quota_gb}
                  onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, quota_gb: e.target.value } : x))} />
              </td>
              <td style={cell}>
                <input style={inp} value={r.user_default_gb}
                  onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, user_default_gb: e.target.value } : x))} />
              </td>
              <td style={cell}>
                <button onClick={() => save(r)} disabled={saving === r.id || !canUpdate}
                  style={{ padding: '3px 12px', background: '#6a1b9a', color: '#fff', border: 'none', borderRadius: 4, cursor: canUpdate ? 'pointer' : 'not-allowed', opacity: canUpdate ? 1 : 0.5 }}>
                  {t('common.save', '儲存')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TenantDrivePage;
